import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Database, Eye, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusBadge } from "@/components/Badges";
import { JobControls, JobProgress, useJobControl } from "@/components/search/JobProgress";
import { api, formatApiError } from "@/lib/api";
import { ACTIVE_STATUSES, SOURCE_LABELS, formatDate } from "@/lib/constants";

const FILTERS = {
  all: () => true,
  active: (j) => ACTIVE_STATUSES.includes(j.status),
  completed: (j) => j.status === "completed",
  failed: (j) => ["failed", "cancelled"].includes(j.status),
};

const JobRow = ({ job, onOpen, onDelete }) => {
  const control = useJobControl();
  return (
    <tr data-testid={`job-row-${job.id}`} className="transition-colors hover:bg-zinc-50">
      <td className="font-mono text-xs text-muted-foreground">{formatDate(job.created_at)}</td>
      <td>
        <div className="font-semibold">{job.keywords}</div>
        <div className="text-xs text-muted-foreground">{job.location}{job.radius_km ? ` · ${job.radius_km} km` : ""}</div>
      </td>
      <td className="max-w-[220px]">
        <div className="flex flex-wrap gap-1">
          {Object.entries(job.sources).filter(([, v]) => v).map(([k]) => (
            <span key={k} className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium">{SOURCE_LABELS[k]}</span>
          ))}
        </div>
      </td>
      <td><StatusBadge status={job.status} testId={`job-status-${job.id}`} /></td>
      <td className="w-40">
        <Progress value={job.progress || 0} className="h-1.5" />
        <div className="mt-1 font-mono text-[10px] text-muted-foreground">{Math.round(job.progress || 0)}%</div>
      </td>
      <td className="font-mono text-sm">{job.stats?.unique ?? 0}<span className="text-muted-foreground"> / {job.stats?.found ?? 0}</span></td>
      <td className="font-mono text-sm">{job.stats?.enriched ?? 0}</td>
      <td>
        <div className="flex flex-wrap items-center gap-1">
          <JobControls job={job} prefix={`job-${job.id}`} />
          <Button size="icon" variant="ghost" data-testid={`job-${job.id}-details-button`} onClick={() => onOpen(job.id)} title="Détails"><Eye className="h-4 w-4" /></Button>
          <Button asChild size="icon" variant="ghost" data-testid={`job-${job.id}-leads-link`} title="Leads"><Link to={`/leads?job_id=${job.id}`}><Database className="h-4 w-4" /></Link></Button>
          {!ACTIVE_STATUSES.includes(job.status) && (
            <>
              <Button size="icon" variant="ghost" data-testid={`job-${job.id}-retry-button`} onClick={() => control(job.id, "retry")} title="Relancer"><RotateCcw className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" data-testid={`job-${job.id}-delete-button`} onClick={() => onDelete(job.id)} title="Supprimer"><Trash2 className="h-4 w-4 text-[#FF3B30]" /></Button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

export default function TaskQueue() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ["jobs"],
    queryFn: () => api.get("/jobs").then((r) => r.data),
    refetchInterval: (q) => ((q.state.data || []).some((j) => ACTIVE_STATUSES.includes(j.status)) ? 2000 : 8000),
  });
  const rows = jobs.filter(FILTERS[filter]);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette recherche de l'historique ? Les leads restent dans la base.")) return;
    try {
      await api.delete(`/jobs/${id}`);
      toast.success("Recherche supprimée de l'historique");
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="task-queue-page">
      <PageHeader index="02" title="Gestionnaire de tâches" subtitle="File d'attente persistée : suivez, mettez en pause, reprenez ou annulez vos recherches de masse." />
      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList>
          <TabsTrigger value="all" data-testid="jobs-filter-all">Toutes ({jobs.length})</TabsTrigger>
          <TabsTrigger value="active" data-testid="jobs-filter-active">Actives ({jobs.filter(FILTERS.active).length})</TabsTrigger>
          <TabsTrigger value="completed" data-testid="jobs-filter-completed">Terminées ({jobs.filter(FILTERS.completed).length})</TabsTrigger>
          <TabsTrigger value="failed" data-testid="jobs-filter-failed">Échouées / annulées ({jobs.filter(FILTERS.failed).length})</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="overflow-x-auto rounded-lg border border-border bg-white shadow-sm">
        <table data-testid="jobs-table" className="data-table w-full whitespace-nowrap text-sm">
          <thead>
            <tr><th>Créée</th><th>Recherche</th><th>Sources</th><th>Statut</th><th>Progression</th><th>Leads</th><th>Enrichis</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {rows.map((job) => <JobRow key={job.id} job={job} onOpen={setOpenId} onDelete={remove} />)}
            {!rows.length && (
              <tr><td colSpan={8} data-testid="jobs-empty" className="py-12 text-center text-muted-foreground">{isLoading ? "Chargement…" : "Aucune recherche pour ce filtre."}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Dialog open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto p-0" data-testid="job-details-dialog">
          <DialogHeader className="sr-only"><DialogTitle>Détails de la recherche</DialogTitle></DialogHeader>
          {openId && <JobProgress jobId={openId} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
